import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Form, Row, Space, Divider, Typography } from 'antd';
import { useResponsive } from '../../hooks/useResponsive';
const { Title } = Typography;
/**
 * Mobile-optimized form layout component
 *
 * @example
 * ```tsx
 * import { Col } from 'antd';
 *
 * <MobileFormLayout
 *   title="Create POI"
 *   subtitle="Add a new point of interest"
 *   actions={<Button type="primary">Save</Button>}
 * >
 *   <Col xs={24} md={12}>
 *     <Form.Item name="title" label="Title">
 *       <Input />
 *     </Form.Item>
 *   </Col>
 * </MobileFormLayout>
 * ```
 */
export const MobileFormLayout = ({ title, subtitle, children, actions, showDividers = true, formProps, gutter, stackOnMobile = true, mobileBreakpoint = 'md', }) => {
    const { isMobile, isTablet } = useResponsive();
    // Determine if we should stack on mobile based on the specified breakpoint
    const shouldStack = stackOnMobile && ((mobileBreakpoint === 'xs' && isMobile) ||
        (mobileBreakpoint === 'sm' && isMobile) ||
        (mobileBreakpoint === 'md' && (isMobile || isTablet)));
    // Responsive gutter
    const responsiveGutter = gutter || (isMobile ? [16, 16] : [24, 24]);
    // Form size
    const formSize = isMobile ? 'large' : 'middle';
    return (_jsxs("div", { style: {
            padding: isMobile ? 16 : 24,
            maxWidth: '100%',
        }, children: [(title || subtitle) && (_jsxs("div", { style: { marginBottom: isMobile ? 16 : 24 }, children: [title && (_jsx(Title, { level: isMobile ? 5 : 4, style: {
                            margin: 0,
                            marginBottom: subtitle ? 8 : 0,
                            fontSize: isMobile ? 18 : 24,
                        }, children: title })), subtitle && (_jsx("div", { style: {
                            color: '#666',
                            fontSize: isMobile ? 14 : 16,
                        }, children: subtitle })), showDividers && _jsx(Divider, { style: { margin: '16px 0' } })] })), _jsx(Form, { layout: "vertical", size: formSize, ...formProps, children: shouldStack ? (
                // Mobile: Stack all children vertically
                _jsx(Space, { direction: "vertical", size: "large", style: { width: '100%' }, children: children })) : (
                // Desktop: Use responsive grid - children will be wrapped in Col by the parent
                _jsx(Row, { gutter: responsiveGutter, children: children })) }), actions && (_jsx("div", { style: {
                    marginTop: isMobile ? 24 : 32,
                    paddingTop: isMobile ? 16 : 24,
                    borderTop: '1px solid #f0f0f0',
                }, children: _jsx(Space, { direction: isMobile ? "vertical" : "horizontal", size: "middle", style: { width: isMobile ? '100%' : 'auto' }, children: actions }) }))] }));
};
export const MobileFormSection = ({ title, children, showDivider = true, spacing = 'medium', }) => {
    const { isMobile } = useResponsive();
    const spacingMap = {
        small: isMobile ? 12 : 16,
        medium: isMobile ? 16 : 24,
        large: isMobile ? 24 : 32,
    };
    return (_jsxs("div", { style: { marginBottom: spacingMap[spacing] }, children: [title && (_jsx(Title, { level: isMobile ? 5 : 4, style: {
                    margin: 0,
                    marginBottom: isMobile ? 8 : 12,
                    fontSize: isMobile ? 16 : 18,
                }, children: title })), _jsx(Space, { direction: "vertical", size: "middle", style: { width: '100%' }, children: children }), showDivider && _jsx(Divider, { style: { margin: '16px 0' } })] }));
};
export default MobileFormLayout;
//# sourceMappingURL=MobileFormLayout.js.map